import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api.ts'
import {
  Search, Package, ShoppingCart, Receipt, Plus, X, Building2,
  Link2, Unlink, ExternalLink, Users as UsersIcon,
} from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  Modal,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  currencyColumn,
  dateColumn,
  textColumn,
  useToast,
  type ColumnDef,
} from '@/components/ui'

interface AlsoProduct {
  sku: string
  name: string
  vendor: string
  category?: string
  price?: number
  currency?: string
  available: boolean
  description?: string
}

interface AlsoSubscription {
  id: string
  productName: string
  sku: string
  quantity: number
  status: 'active' | 'suspended' | 'cancelled'
  renewalDate?: string
  monthlyPrice?: number
}

interface AlsoOrder {
  id: string
  status: string
  createdAt: string
  total?: number
  lines: Array<{ sku: string; name: string; quantity: number; unitPrice: number }>
}

interface AlsoInvoice {
  id: string
  invoiceNumber: string
  date: string
  dueDate?: string
  total: number
  currency: string
  status: 'paid' | 'unpaid' | 'overdue'
  downloadUrl?: string
}

interface OrderLine {
  sku: string
  quantity: number
  endCustomerRef?: string
}

// ─── Schritt 3: Companies + Users (echte Simple-API) ────────────────────────

interface AlsoCompanyListed {
  AccountId: number
  ParentAccountId?: number
  CompanyName: string
  Email?: string
  ContactPhone?: string
  ContactPerson?: string
  Address?: string
  City?: string
  Zip?: string
  Country?: string
  VATID?: string
  CustomerId?: string
  Currency?: string
  AccountState?: string
  Language?: string
  /** vom Backend angereichert: lokaler SHM-Kunde, falls verknuepft */
  _mapping?: { id: string; name: string } | null
}

interface AlsoUserListed {
  AccountId: number
  ParentAccountId?: number
  Username: string
  Email?: string
  FirstName?: string
  LastName?: string
  AccountState?: string
  LastLogon?: string
}

interface ShmCustomerLite {
  id: string
  name: string
  alsoCustomerId?: string | null
}

type TabKey = 'customers' | 'subscriptions' | 'products' | 'orders' | 'invoices'

interface NewCompanyForm {
  CompanyName: string
  Address: string
  City: string
  Zip: string
  Country: string
  Email: string
  Domain: string  // im Form als Komma-Liste, beim Submit zu string[] gesplittet
  VATID: string
  ContactPhone: string
  ContactPerson: string
  Currency: string
  Language: string
  CreateDefaultAdminUser: boolean
  shmAction: 'create' | 'link' | 'none'
}

interface CreateCompanyPayload extends Omit<NewCompanyForm, 'Domain'> {
  Domain: string[]
}

interface NewUserForm {
  Username: string
  Email: string
  FirstName: string
  LastName: string
}

function emptyCompanyForm(): NewCompanyForm {
  return {
    CompanyName: '',
    Address: '',
    City: '',
    Zip: '',
    Country: 'Germany',
    Email: '',
    Domain: '',
    VATID: '',
    ContactPhone: '',
    ContactPerson: '',
    Currency: 'EUR',
    Language: 'de',
    CreateDefaultAdminUser: false,
    shmAction: 'create',
  }
}

function emptyUserForm(): NewUserForm {
  return { Username: '', Email: '', FirstName: '', LastName: '' }
}

const subscriptionColumns: ColumnDef<AlsoSubscription, unknown>[] = [
  textColumn<AlsoSubscription>('productName', 'Produkt'),
  {
    accessorKey: 'sku',
    header: 'SKU',
    cell: ({ getValue }) => (
      <span className="font-mono text-caption text-gray-500">{getValue() as string}</span>
    ),
  },
  {
    accessorKey: 'quantity',
    header: 'Menge',
    size: 80,
    cell: ({ getValue }) => <span className="tabular-nums">{getValue() as number}</span>,
  },
  currencyColumn<AlsoSubscription>('monthlyPrice', 'Monatlich', { size: 120 }),
  dateColumn<AlsoSubscription>('renewalDate', 'Erneuerung', { size: 120 }),
  {
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cell: ({ getValue }) => <StatusBadge status={getValue() as string} />,
  },
]

const orderColumns: ColumnDef<AlsoOrder, unknown>[] = [
  {
    accessorKey: 'id',
    header: 'Bestell-Nr.',
    cell: ({ getValue }) => (
      <span className="font-mono text-caption text-gray-900">{getValue() as string}</span>
    ),
  },
  dateColumn<AlsoOrder>('createdAt', 'Datum', { size: 120 }),
  {
    id: 'positions',
    header: 'Positionen',
    cell: ({ row }) =>
      row.original.lines.map((l) => `${l.name} (${l.quantity}x)`).join(', '),
  },
  currencyColumn<AlsoOrder>('total', 'Gesamt', { size: 120 }),
  {
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cell: ({ getValue }) => <StatusBadge status={getValue() as string} />,
  },
]

const invoiceColumns: ColumnDef<AlsoInvoice, unknown>[] = [
  textColumn<AlsoInvoice>('invoiceNumber', 'Rechnung'),
  dateColumn<AlsoInvoice>('date', 'Datum', { size: 120 }),
  dateColumn<AlsoInvoice>('dueDate', 'Fällig', { size: 120 }),
  {
    accessorKey: 'total',
    header: 'Betrag',
    size: 140,
    cell: ({ row }) => {
      const { total, currency } = row.original
      return (
        <span className="tabular-nums font-medium">
          {total.toLocaleString('de', { minimumFractionDigits: 2 })} {currency}
        </span>
      )
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cell: ({ getValue }) => <StatusBadge status={getValue() as string} />,
  },
]

export function AlsoMarketplacePage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState<TabKey>('customers')
  const [search, setSearch] = useState('')
  const [showOrderForm, setShowOrderForm] = useState(false)
  const [orderLines, setOrderLines] = useState<OrderLine[]>([{ sku: '', quantity: 1 }])

  // ─── Customers / Users state ──────────────────────────────────────────────
  const [customerSearch, setCustomerSearch] = useState('')
  const [detailCompany, setDetailCompany] = useState<AlsoCompanyListed | null>(null)
  const [linkTarget, setLinkTarget] = useState<AlsoCompanyListed | null>(null)
  const [shmSearch, setShmSearch] = useState('')
  const [showCreateCompany, setShowCreateCompany] = useState(false)
  const [newCompany, setNewCompany] = useState<NewCompanyForm>(emptyCompanyForm())
  const [showCreateUser, setShowCreateUser] = useState(false)
  const [newUser, setNewUser] = useState<NewUserForm>(emptyUserForm())

  const companies = useQuery<AlsoCompanyListed[]>({
    queryKey: ['also-companies'],
    queryFn: () => api.get('/also/companies').then((r) => r.data),
    enabled: activeTab === 'customers',
    retry: false,
  })

  const companyUsers = useQuery<AlsoUserListed[]>({
    queryKey: ['also-company-users', detailCompany?.AccountId],
    queryFn: () =>
      api.get(`/also/companies/${detailCompany?.AccountId}/users`).then((r) => r.data),
    enabled: !!detailCompany,
    retry: false,
  })

  const shmCustomers = useQuery<ShmCustomerLite[]>({
    queryKey: ['shm-customers', shmSearch],
    queryFn: () =>
      api.get('/customers', { params: { search: shmSearch } }).then((r) =>
        r.data.map((c: { id: string; name: string; also_customer_id?: string; alsoCustomerId?: string }) => ({
          id: c.id,
          name: c.name,
          alsoCustomerId: c.alsoCustomerId ?? c.also_customer_id ?? null,
        })),
      ),
    enabled: !!linkTarget,
    retry: false,
  })

  const linkMutation = useMutation({
    mutationFn: ({ shmCustomerId, alsoAccountId }: { shmCustomerId: string; alsoAccountId: number }) =>
      api
        .post(`/also/customers/${shmCustomerId}/link`, { alsoAccountId })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['also-companies'] })
      setLinkTarget(null)
      toast({ title: 'Kunde verknüpft', tone: 'success' })
    },
    onError: () => {
      toast({ title: 'Verknüpfen fehlgeschlagen', tone: 'danger' })
    },
  })

  const unlinkMutation = useMutation({
    mutationFn: (shmCustomerId: string) =>
      api.delete(`/also/customers/${shmCustomerId}/link`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['also-companies'] })
      toast({ title: 'Verknüpfung gelöst', tone: 'success' })
    },
    onError: () => {
      toast({ title: 'Lösen fehlgeschlagen', tone: 'danger' })
    },
  })

  // ─── ParentAccountId-Aufloesung (Trockenlauf, fuer Diagnose im Dialog) ───
  interface ParentResolution {
    source: 'existing-company' | 'config' | 'none'
    parentAccountId: number | null
    configResellerAccountId: number | null
    existingCompany: { alsoAccountId: string; customerName: string } | null
    mismatch: boolean
  }
  const parentResolution = useQuery<ParentResolution>({
    queryKey: ['also-parent-resolution'],
    queryFn: () => api.get('/also/companies/parent-resolution').then((r) => r.data),
    enabled: showCreateCompany,
    staleTime: 60_000,
    retry: false,
  })

  // ─── Live-Duplikatspruefung beim Tippen der VAT-ID ────────────────────────
  const vatCheck = useQuery<AlsoCompanyListed | null>({
    queryKey: ['also-vat-check', newCompany.VATID],
    queryFn: () =>
      api
        .get('/also/companies/by-vat', { params: { vatId: newCompany.VATID } })
        .then((r) => r.data),
    enabled: showCreateCompany && newCompany.VATID.trim().length >= 5,
    retry: false,
    staleTime: 30_000,
  })

  const createCompanyMutation = useMutation({
    mutationFn: (input: CreateCompanyPayload) =>
      api.post('/also/companies', input).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['also-companies'] })
      setShowCreateCompany(false)
      setNewCompany(emptyCompanyForm())
      toast({ title: 'Kunde in ALSO angelegt', tone: 'success' })
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string; error?: string } } }
      const msg = e?.response?.data?.message ?? e?.response?.data?.error ?? 'Anlegen fehlgeschlagen'
      toast({ title: 'Anlegen fehlgeschlagen', description: msg, tone: 'danger' })
    },
  })

  const createUserMutation = useMutation({
    mutationFn: ({ accountId, body }: { accountId: number; body: NewUserForm }) =>
      api.post(`/also/companies/${accountId}/users`, body).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['also-company-users'] })
      setShowCreateUser(false)
      setNewUser(emptyUserForm())
      toast({ title: 'Benutzer angelegt', tone: 'success' })
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string; error?: string } } }
      const msg = e?.response?.data?.message ?? e?.response?.data?.error ?? 'Anlegen fehlgeschlagen'
      toast({ title: 'Benutzer anlegen fehlgeschlagen', description: msg, tone: 'danger' })
    },
  })

  const filteredCompanies = useMemo(() => {
    const list = companies.data ?? []
    const q = customerSearch.trim().toLowerCase()
    if (!q) return list
    return list.filter((c) =>
      [c.CompanyName, c.City, c.Country, c.VATID, c.Email, String(c.AccountId)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    )
  }, [companies.data, customerSearch])

  const customerColumns = useMemo<ColumnDef<AlsoCompanyListed, unknown>[]>(() => [
    {
      accessorKey: 'AccountId',
      header: 'AccountId',
      size: 100,
      cell: ({ getValue }) => (
        <span className="font-mono text-caption text-gray-600">{getValue() as number}</span>
      ),
    },
    {
      accessorKey: 'ParentAccountId',
      header: 'ParentAccountId',
      size: 130,
      cell: ({ getValue }) => {
        const v = getValue() as number | undefined
        return v ? (
          <span className="font-mono text-caption text-gray-600">{v}</span>
        ) : (
          <span className="text-caption text-gray-400 italic">—</span>
        )
      },
    },
    {
      accessorKey: 'CompanyName',
      header: 'Firma',
      cell: ({ row }) => (
        <button
          type="button"
          className="text-left hover:underline text-gray-900 font-medium"
          onClick={() => setDetailCompany(row.original)}
        >
          {row.original.CompanyName}
        </button>
      ),
    },
    {
      id: 'location',
      header: 'Ort',
      cell: ({ row }) => {
        const c = row.original
        return [c.Zip, c.City, c.Country].filter(Boolean).join(' ')
      },
    },
    { accessorKey: 'VATID', header: 'USt-ID', size: 140 },
    {
      id: 'mapping',
      header: 'Verknüpfung',
      size: 240,
      cell: ({ row }) => {
        const m = row.original._mapping
        return m ? (
          <Badge variant="success" className="font-normal">
            <Link2 className="w-3 h-3 mr-1 inline" />
            {m.name}
          </Badge>
        ) : (
          <span className="text-caption text-gray-400 italic">nicht verknüpft</span>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      size: 80,
      cell: ({ row }) => {
        const m = row.original._mapping
        return (
          <div className="flex items-center justify-end gap-1">
            {m ? (
              <IconButton
                size="xs"
                variant="ghost"
                aria-label="Verknüpfung lösen"
                icon={<Unlink className="w-3.5 h-3.5" />}
                onClick={() => unlinkMutation.mutate(m.id)}
              />
            ) : (
              <IconButton
                size="xs"
                variant="ghost"
                aria-label="Mit SHM-Kunde verknüpfen"
                icon={<Link2 className="w-3.5 h-3.5" />}
                onClick={() => setLinkTarget(row.original)}
              />
            )}
            <IconButton
              size="xs"
              variant="ghost"
              aria-label="Details"
              icon={<ExternalLink className="w-3.5 h-3.5" />}
              onClick={() => setDetailCompany(row.original)}
            />
          </div>
        )
      },
    },
  ], [unlinkMutation])

  const products = useQuery<AlsoProduct[]>({
    queryKey: ['also-products', search],
    queryFn: () => api.get('/also/products', { params: { q: search } }).then((r) => r.data),
    enabled: activeTab === 'products',
    retry: false,
  })

  const subscriptions = useQuery<AlsoSubscription[]>({
    queryKey: ['also-subscriptions'],
    queryFn: () => api.get('/also/subscriptions').then((r) => r.data),
    enabled: activeTab === 'subscriptions',
    retry: false,
  })

  const orders = useQuery<AlsoOrder[]>({
    queryKey: ['also-orders'],
    queryFn: () => api.get('/also/orders').then((r) => r.data),
    enabled: activeTab === 'orders',
    retry: false,
  })

  const invoices = useQuery<AlsoInvoice[]>({
    queryKey: ['also-invoices'],
    queryFn: () => api.get('/also/invoices').then((r) => r.data),
    enabled: activeTab === 'invoices',
    retry: false,
  })

  const createOrder = useMutation({
    mutationFn: (lines: OrderLine[]) =>
      api.post('/also/orders', { lines }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['also-orders'] })
      setShowOrderForm(false)
      setOrderLines([{ sku: '', quantity: 1 }])
      setActiveTab('orders')
      toast({ title: 'Bestellung erstellt', tone: 'success' })
    },
    onError: () => {
      toast({
        title: 'Fehler beim Bestellen',
        description: 'Bitte erneut versuchen.',
        tone: 'danger',
      })
    },
  })

  const orderFormValid = orderLines.some((l) => l.sku.trim() && l.quantity > 0)

  return (
    <div className="space-y-4">
      <PageHeader
        title="ALSO Marketplace"
        description="Kunden, Abonnements, Bestellungen und Rechnungen"
        actions={
          activeTab === 'customers' ? (
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShowCreateCompany(true)}
            >
              Kunde anlegen
            </Button>
          ) : activeTab === 'orders' ? (
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShowOrderForm(true)}
            >
              Neue Bestellung
            </Button>
          ) : null
        }
      />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="customers">
            <Building2 className="w-4 h-4" />
            Kunden
          </TabsTrigger>
          <TabsTrigger value="subscriptions">
            <Package className="w-4 h-4" />
            Abonnements
          </TabsTrigger>
          <TabsTrigger value="products">
            <Search className="w-4 h-4" />
            Produktkatalog
          </TabsTrigger>
          <TabsTrigger value="orders">
            <ShoppingCart className="w-4 h-4" />
            Bestellungen
          </TabsTrigger>
          <TabsTrigger value="invoices">
            <Receipt className="w-4 h-4" />
            Rechnungen
          </TabsTrigger>
        </TabsList>

        <TabsContent value="customers" className="space-y-3">
          <Input
            leadingIcon={<Search />}
            placeholder="Filter nach Name, Ort, USt-ID oder AccountId …"
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.currentTarget.value)}
          />
          <DataTable<AlsoCompanyListed>
            data={filteredCompanies}
            columns={customerColumns}
            getRowId={(c) => String(c.AccountId)}
            loading={companies.isLoading}
            error={
              companies.isError
                ? 'ALSO Marketplace nicht konfiguriert oder Verbindungsfehler.'
                : null
            }
            onRetry={() => companies.refetch()}
            emptyState={
              <EmptyState
                title="Keine ALSO-Kunden gefunden"
                description="Unter dem Reseller sind aktuell keine Companies hinterlegt."
              />
            }
          />
        </TabsContent>

        <TabsContent value="subscriptions">
          <DataTable<AlsoSubscription>
            data={subscriptions.data ?? []}
            columns={subscriptionColumns}
            getRowId={(s) => s.id}
            loading={subscriptions.isLoading}
            error={
              subscriptions.isError
                ? 'ALSO Marketplace nicht konfiguriert oder Verbindungsfehler.'
                : null
            }
            onRetry={() => subscriptions.refetch()}
            emptyState={<EmptyState title="Keine Abonnements gefunden" />}
          />
        </TabsContent>

        <TabsContent value="products" className="space-y-4">
          <Input
            leadingIcon={<Search />}
            placeholder="Produkt suchen …"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />

          {products.isLoading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
          ) : products.isError ? (
            <Card>
              <Card.Body>
                <ErrorState
                  title="ALSO Marketplace nicht verfügbar"
                  description="Entweder nicht konfiguriert oder Verbindungsfehler."
                  onRetry={() => products.refetch()}
                />
              </Card.Body>
            </Card>
          ) : (products.data ?? []).length === 0 ? (
            <Card>
              <Card.Body>
                <EmptyState
                  title="Keine Produkte gefunden"
                  description="Versuche einen anderen Suchbegriff."
                />
              </Card.Body>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {(products.data ?? []).map((p) => (
                <Card key={p.sku}>
                  <Card.Body>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-body font-semibold text-gray-900 truncate">{p.name}</p>
                        <p className="text-caption text-gray-500 mt-0.5">
                          {p.vendor} · {p.sku}
                        </p>
                      </div>
                      <Package
                        className={`w-5 h-5 flex-shrink-0 ${
                          p.available ? 'text-green-500' : 'text-gray-300'
                        }`}
                      />
                    </div>
                    {p.description ? (
                      <p className="text-caption text-gray-500 mt-2 line-clamp-2">
                        {p.description}
                      </p>
                    ) : null}
                    {p.price != null ? (
                      <p className="text-body font-bold text-gray-900 mt-3">
                        {p.price.toLocaleString('de', { minimumFractionDigits: 2 })}{' '}
                        {p.currency ?? 'EUR'}
                      </p>
                    ) : null}
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders">
          <DataTable<AlsoOrder>
            data={orders.data ?? []}
            columns={orderColumns}
            getRowId={(o) => o.id}
            loading={orders.isLoading}
            error={
              orders.isError
                ? 'ALSO Marketplace nicht konfiguriert oder Verbindungsfehler.'
                : null
            }
            onRetry={() => orders.refetch()}
            emptyState={<EmptyState title="Keine Bestellungen gefunden" />}
          />
        </TabsContent>

        <TabsContent value="invoices">
          <DataTable<AlsoInvoice>
            data={invoices.data ?? []}
            columns={invoiceColumns}
            getRowId={(i) => i.id}
            loading={invoices.isLoading}
            error={
              invoices.isError
                ? 'ALSO Marketplace nicht konfiguriert oder Verbindungsfehler.'
                : null
            }
            onRetry={() => invoices.refetch()}
            emptyState={<EmptyState title="Keine Rechnungen gefunden" />}
          />
        </TabsContent>
      </Tabs>

      <Modal
        open={showOrderForm}
        onOpenChange={(open) => {
          if (!open) setShowOrderForm(false)
        }}
        title="Neue Bestellung"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowOrderForm(false)}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              loading={createOrder.isPending}
              disabled={!orderFormValid}
              onClick={() => {
                const validLines = orderLines.filter((l) => l.sku.trim() && l.quantity > 0)
                if (validLines.length > 0) createOrder.mutate(validLines)
              }}
            >
              {createOrder.isPending ? 'Wird bestellt …' : 'Bestellen'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {orderLines.map((line, idx) => (
            <div key={idx} className="flex items-end gap-2">
              <Input
                label={idx === 0 ? 'SKU' : undefined}
                value={line.sku}
                onChange={(e) => {
                  const updated = [...orderLines]
                  updated[idx] = { ...updated[idx], sku: e.currentTarget.value }
                  setOrderLines(updated)
                }}
                placeholder="z.B. MS-365-BUS-STD"
                wrapperClassName="flex-1"
              />
              <Input
                label={idx === 0 ? 'Menge' : undefined}
                type="number"
                min={1}
                value={line.quantity}
                onChange={(e) => {
                  const updated = [...orderLines]
                  updated[idx] = { ...updated[idx], quantity: Number(e.currentTarget.value) }
                  setOrderLines(updated)
                }}
                wrapperClassName="w-24"
              />
              {orderLines.length > 1 ? (
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={<X />}
                  aria-label="Position entfernen"
                  onClick={() => setOrderLines(orderLines.filter((_, i) => i !== idx))}
                />
              ) : null}
            </div>
          ))}
          <Button
            variant="link"
            size="sm"
            leadingIcon={<Plus className="w-3.5 h-3.5" />}
            onClick={() => setOrderLines([...orderLines, { sku: '', quantity: 1 }])}
          >
            Position hinzufügen
          </Button>
        </div>
      </Modal>

      {/* ─── Company-Details + ALSO-Benutzer ─────────────────────────── */}
      <Modal
        open={!!detailCompany}
        onOpenChange={(open) => { if (!open) setDetailCompany(null) }}
        title={detailCompany?.CompanyName ?? 'Kunde'}
        size="lg"
      >
        {detailCompany && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-caption">
              <DetailItem label="AccountId" value={String(detailCompany.AccountId)} mono />
              <DetailItem label="Status" value={detailCompany.AccountState} />
              <DetailItem label="USt-ID" value={detailCompany.VATID} />
              <DetailItem label="Währung" value={detailCompany.Currency} />
              <DetailItem label="Sprache" value={detailCompany.Language} />
              <DetailItem label="E-Mail" value={detailCompany.Email} />
              <DetailItem label="Telefon" value={detailCompany.ContactPhone} />
              <DetailItem label="Ansprechpartner" value={detailCompany.ContactPerson} />
              <DetailItem
                label="Adresse"
                value={[detailCompany.Address, detailCompany.Zip, detailCompany.City, detailCompany.Country]
                  .filter(Boolean)
                  .join(', ')}
                wide
              />
              <DetailItem
                label="SHM-Verknüpfung"
                value={detailCompany._mapping?.name ?? '— nicht verknüpft —'}
                wide
              />
            </div>

            <Card>
              <Card.Header>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <UsersIcon className="w-4 h-4 text-gray-500" />
                    <span className="text-small font-medium">ALSO-Benutzer dieser Firma</span>
                  </div>
                  <Button
                    size="xs"
                    variant="secondary"
                    leadingIcon={<Plus className="w-3 h-3" />}
                    onClick={() => setShowCreateUser(true)}
                  >
                    Benutzer
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {companyUsers.isLoading ? (
                  <Skeleton className="h-20" />
                ) : companyUsers.isError ? (
                  <ErrorState
                    title="Benutzer konnten nicht geladen werden"
                    onRetry={() => companyUsers.refetch()}
                  />
                ) : (companyUsers.data ?? []).length === 0 ? (
                  <p className="text-caption text-gray-400 italic">
                    Keine Benutzer in ALSO hinterlegt.
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {(companyUsers.data ?? []).map((u) => (
                      <li key={u.AccountId} className="py-2 flex items-center justify-between">
                        <div>
                          <p className="text-small font-medium text-gray-900">
                            {[u.FirstName, u.LastName].filter(Boolean).join(' ') || u.Username}
                          </p>
                          <p className="text-caption text-gray-500">
                            {u.Email ?? u.Username}
                            {u.LastLogon ? ` · zuletzt: ${u.LastLogon.slice(0, 10)}` : ''}
                          </p>
                        </div>
                        <StatusBadge status={u.AccountState ?? 'unknown'} />
                      </li>
                    ))}
                  </ul>
                )}
              </Card.Body>
            </Card>
          </div>
        )}
      </Modal>

      {/* ─── Neuen ALSO-Kunden anlegen ───────────────────────────── */}
      <Modal
        open={showCreateCompany}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateCompany(false)
            setNewCompany(emptyCompanyForm())
          }
        }}
        title="Neuen Kunden in ALSO anlegen"
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreateCompany(false)
                setNewCompany(emptyCompanyForm())
              }}
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              loading={createCompanyMutation.isPending}
              disabled={
                !newCompany.CompanyName ||
                !newCompany.Address ||
                !newCompany.City ||
                !newCompany.Zip ||
                !newCompany.Country ||
                !newCompany.Email ||
                !newCompany.Domain
              }
              onClick={() => {
                const payload: CreateCompanyPayload = {
                  ...newCompany,
                  Domain: newCompany.Domain
                    .split(/[,\s]+/)
                    .map((d) => d.trim())
                    .filter(Boolean),
                }
                createCompanyMutation.mutate(payload)
              }}
            >
              Anlegen
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {/* Diagnose: Wie wird ParentAccountId aufgeloest? */}
          {parentResolution.isLoading && (
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-caption text-slate-600">
              ParentAccountId wird ermittelt …
            </div>
          )}
          {parentResolution.data && (
            <div
              className={
                parentResolution.data.source === 'none'
                  ? 'rounded border border-red-200 bg-red-50 p-3 text-caption text-red-900'
                  : parentResolution.data.mismatch
                    ? 'rounded border border-amber-200 bg-amber-50 p-3 text-caption text-amber-900'
                    : 'rounded border border-emerald-200 bg-emerald-50 p-3 text-caption text-emerald-900'
              }
            >
              <div className="font-semibold mb-1">ParentAccountId-Auflösung</div>
              {parentResolution.data.source === 'existing-company' && (
                <div>
                  Quelle: <strong>aus bestehendem Kunden</strong> – „
                  {parentResolution.data.existingCompany?.customerName}" (ALSO-AccountId{' '}
                  {parentResolution.data.existingCompany?.alsoAccountId})
                  <br />
                  Wert: <strong>{parentResolution.data.parentAccountId}</strong>
                  {parentResolution.data.configResellerAccountId !== null && (
                    <>
                      <br />
                      Adapter-Config: {parentResolution.data.configResellerAccountId}
                      {parentResolution.data.mismatch ? (
                        <strong> ⚠ weicht ab</strong>
                      ) : (
                        <> ✓ übereinstimmend</>
                      )}
                    </>
                  )}
                </div>
              )}
              {parentResolution.data.source === 'config' && (
                <div>
                  Quelle: <strong>Adapter-Config</strong> (kein verknuepfter ALSO-Kunde
                  zum Zurueckliesen vorhanden)
                  <br />
                  Wert: <strong>{parentResolution.data.parentAccountId}</strong>
                </div>
              )}
              {parentResolution.data.source === 'none' && (
                <div>
                  <strong>Keine ParentAccountId verfügbar.</strong> Es existiert weder
                  ein verknuepfter ALSO-Kunde, aus dem zurueckgelesen werden koennte,
                  noch ist eine `resellerAccountId` in der Adapter-Config gesetzt. Das
                  Anlegen wird fehlschlagen.
                </div>
              )}
            </div>
          )}
          {parentResolution.error && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-caption text-red-900">
              <strong>ParentAccountId-Aufloesung fehlgeschlagen:</strong>{' '}
              {(parentResolution.error as Error).message}
            </div>
          )}
          {vatCheck.data && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-caption text-amber-900">
              <strong>Achtung:</strong> Es existiert bereits eine ALSO-Company mit
              dieser USt-ID — „{vatCheck.data.CompanyName}" (AccountId&nbsp;
              {vatCheck.data.AccountId}). Pruefe, ob du den vorhandenen Kunden
              verknuepfen willst statt neu anzulegen.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Firmenname *"
              value={newCompany.CompanyName}
              onChange={(e) => setNewCompany({ ...newCompany, CompanyName: e.currentTarget.value })}
            />
            <Input
              label="USt-ID"
              placeholder="DE123456789"
              value={newCompany.VATID}
              onChange={(e) => setNewCompany({ ...newCompany, VATID: e.currentTarget.value })}
              hint={vatCheck.isFetching ? 'Pruefe …' : undefined}
            />
            <Input
              label="Strasse / Adresse *"
              value={newCompany.Address}
              onChange={(e) => setNewCompany({ ...newCompany, Address: e.currentTarget.value })}
              className="col-span-2"
            />
            <Input
              label="PLZ *"
              value={newCompany.Zip}
              onChange={(e) => setNewCompany({ ...newCompany, Zip: e.currentTarget.value })}
            />
            <Input
              label="Stadt *"
              value={newCompany.City}
              onChange={(e) => setNewCompany({ ...newCompany, City: e.currentTarget.value })}
            />
            <Input
              label="Land *"
              value={newCompany.Country}
              onChange={(e) => setNewCompany({ ...newCompany, Country: e.currentTarget.value })}
            />
            <Input
              label="Sprache"
              value={newCompany.Language}
              onChange={(e) => setNewCompany({ ...newCompany, Language: e.currentTarget.value })}
              hint="z.B. de, en"
            />
            <Input
              label="E-Mail *"
              type="email"
              value={newCompany.Email}
              onChange={(e) => setNewCompany({ ...newCompany, Email: e.currentTarget.value })}
            />
            <Input
              label="Telefon"
              value={newCompany.ContactPhone}
              onChange={(e) => setNewCompany({ ...newCompany, ContactPhone: e.currentTarget.value })}
            />
            <Input
              label="Ansprechpartner"
              value={newCompany.ContactPerson}
              onChange={(e) => setNewCompany({ ...newCompany, ContactPerson: e.currentTarget.value })}
              className="col-span-2"
            />
            <Input
              label="Domain(s) *"
              value={newCompany.Domain}
              onChange={(e) => setNewCompany({ ...newCompany, Domain: e.currentTarget.value })}
              placeholder="kunde.de, kunde.com"
              hint="Kommagetrennt"
              className="col-span-2"
            />
            <Input
              label="Waehrung"
              value={newCompany.Currency}
              onChange={(e) => setNewCompany({ ...newCompany, Currency: e.currentTarget.value })}
            />
            <label className="flex items-center gap-2 col-span-2 mt-1">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={newCompany.CreateDefaultAdminUser}
                onChange={(e) =>
                  setNewCompany({ ...newCompany, CreateDefaultAdminUser: e.currentTarget.checked })
                }
              />
              <span className="text-caption text-gray-700">
                Default-Admin-User in ALSO automatisch anlegen (E-Mail wird benachrichtigt)
              </span>
            </label>
          </div>

          <div className="rounded border border-gray-200 bg-gray-50 p-3 space-y-2">
            <p className="text-caption font-medium text-gray-700">Was soll im SHM passieren?</p>
            <div className="space-y-1">
              {(
                [
                  ['create', 'Neuen SHM-Kunden anlegen und mit ALSO verknuepfen'],
                  ['none', 'Nur in ALSO anlegen, nichts im SHM'],
                ] as const
              ).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="shmAction"
                    checked={newCompany.shmAction === value}
                    onChange={() => setNewCompany({ ...newCompany, shmAction: value })}
                  />
                  <span className="text-caption text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* ─── ALSO-Benutzer anlegen ───────────────────────────────── */}
      <Modal
        open={showCreateUser}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateUser(false)
            setNewUser(emptyUserForm())
          }
        }}
        title={detailCompany ? `Benutzer fuer ${detailCompany.CompanyName}` : 'Benutzer anlegen'}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreateUser(false)
                setNewUser(emptyUserForm())
              }}
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              loading={createUserMutation.isPending}
              disabled={
                !detailCompany ||
                !newUser.Username ||
                !newUser.Email ||
                !newUser.FirstName ||
                !newUser.LastName
              }
              onClick={() => {
                if (!detailCompany) return
                createUserMutation.mutate({
                  accountId: detailCompany.AccountId,
                  body: newUser,
                })
              }}
            >
              Anlegen
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Vorname *"
            value={newUser.FirstName}
            onChange={(e) => setNewUser({ ...newUser, FirstName: e.currentTarget.value })}
          />
          <Input
            label="Nachname *"
            value={newUser.LastName}
            onChange={(e) => setNewUser({ ...newUser, LastName: e.currentTarget.value })}
          />
          <Input
            label="E-Mail *"
            type="email"
            value={newUser.Email}
            onChange={(e) => {
              const v = e.currentTarget.value
              setNewUser({
                ...newUser,
                Email: v,
                // Username = Email als Default, aber nur wenn noch leer oder gleich
                Username: !newUser.Username || newUser.Username === newUser.Email ? v : newUser.Username,
              })
            }}
          />
          <Input
            label="Username *"
            value={newUser.Username}
            onChange={(e) => setNewUser({ ...newUser, Username: e.currentTarget.value })}
            hint="Login-Name fuer das ALSO Marketplace-Portal (idR. = E-Mail)"
          />
        </div>
      </Modal>

      {/* ─── Mit SHM-Kunde verknuepfen ─────────────────────────────── */}
      <Modal
        open={!!linkTarget}
        onOpenChange={(open) => {
          if (!open) {
            setLinkTarget(null)
            setShmSearch('')
          }
        }}
        title={linkTarget ? `„${linkTarget.CompanyName}" verknüpfen mit …` : 'Verknüpfen'}
        size="md"
      >
        {linkTarget && (
          <div className="space-y-3">
            <Input
              leadingIcon={<Search />}
              placeholder="SHM-Kunden suchen …"
              value={shmSearch}
              onChange={(e) => setShmSearch(e.currentTarget.value)}
            />
            {shmCustomers.isLoading ? (
              <Skeleton className="h-32" />
            ) : (
              <ul className="max-h-96 overflow-auto divide-y divide-gray-100">
                {(shmCustomers.data ?? []).map((c) => {
                  const alreadyLinked = c.alsoCustomerId && String(c.alsoCustomerId) !== String(linkTarget.AccountId)
                  return (
                    <li
                      key={c.id}
                      className="py-2 flex items-center justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <p className="text-small font-medium text-gray-900 truncate">{c.name}</p>
                        {alreadyLinked && (
                          <p className="text-caption text-amber-700">
                            bereits mit ALSO-AccountId {c.alsoCustomerId} verknüpft
                          </p>
                        )}
                      </div>
                      <Button
                        size="xs"
                        variant={alreadyLinked ? 'secondary' : 'primary'}
                        onClick={() =>
                          linkMutation.mutate({
                            shmCustomerId: c.id,
                            alsoAccountId: linkTarget.AccountId,
                          })
                        }
                        loading={linkMutation.isPending}
                      >
                        Verknüpfen
                      </Button>
                    </li>
                  )
                })}
                {(shmCustomers.data ?? []).length === 0 && (
                  <li className="py-3 text-caption text-gray-400 italic text-center">
                    Keine SHM-Kunden gefunden.
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

function DetailItem({
  label,
  value,
  mono,
  wide,
}: {
  label: string
  value?: string | null
  mono?: boolean
  wide?: boolean
}) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <p className="text-caption text-gray-500">{label}</p>
      <p className={`text-small text-gray-900 ${mono ? 'font-mono' : ''}`}>
        {value ?? <span className="text-gray-400 italic">—</span>}
      </p>
    </div>
  )
}
